import { EmptyState } from '@/components/common/EmptyState';
import { MOTION } from '@/components/common/motion';
import { SelectionCheckbox } from '@/components/common/SelectionCheckbox';
import { CHIP } from '@/components/common/surfaces';
import { ModelPreview } from '@/components/project-detail/ModelPreview';
import { CollisionStatusTag } from '@/components/projects/CollisionStatusTag';
import type { ProjectListItem, SortConfig, SortField } from '@/types/project.types';
import type { RuleSetListItem } from '@/types/ruleSet.types';
import { extractModelIdFromUrl, formatDateTime, formatUserName } from '@/utils/formatters';
import { projectRuleSetInfo } from '@/utils/ruleSets';
import { TABLE_CARD, TABLE_CELL, TABLE_HEADER_CELL, TABLE_HEADER_ROW, TABLE_ROW } from '@/utils/stickyColumns';
import {
  CalendarBlank,
  CaretDown,
  CaretUp,
  FolderOpen,
  ListChecks,
  User,
  Warning,
  WarningCircle,
} from '@phosphor-icons/react';
import { Tooltip } from 'primereact/tooltip';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/** Header cells line up with `TABLE_CELL`'s 12 px gutter; the recipe itself carries type only. */
const HEADER_CELL = `${TABLE_HEADER_CELL} px-3 text-left`;

/** The button inside a sortable header. Sentence case, and the caret only on the active column. */
const SORT_BUTTON = `inline-flex items-center gap-1 rounded text-xs font-semibold text-gray-500 hover:text-gray-900 active:scale-[0.98] ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`;

interface ProjectsTableProps {
  projects: ProjectListItem[];
  selectedProjectIds: Set<string>;
  onProjectClick: (projectId: string) => void;
  onProjectSelect: (projectId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onUserClick?: (ownerId: string) => void;
  sortConfig?: SortConfig;
  onSortChange?: (config: SortConfig) => void;
  /** Every rule set the backend offers, to show a project's set by title. */
  ruleSets?: RuleSetListItem[];
}

/**
 * The projects list as a table.
 *
 * Sorting moved off the `th` and onto a real `button` inside it, with `aria-sort` on the cell —
 * a header that was a `div` with `role="button"` announced nothing about the current order. The
 * caret is drawn only on the column actually in force, so the header reads as data rather than as
 * a row of controls.
 */
export const ProjectsTable: FC<ProjectsTableProps> = ({
  projects,
  selectedProjectIds,
  onProjectClick,
  onProjectSelect,
  onSelectAll,
  onUserClick,
  sortConfig,
  onSortChange,
  ruleSets = [],
}) => {
  const { t } = useTranslation();
  const allSelected = projects.length > 0 && projects.every((p) => selectedProjectIds.has(p.id));
  const someSelected = selectedProjectIds.size > 0 && !allSelected;

  const handleSort = (field: SortField) => {
    if (!onSortChange || !sortConfig) return;

    const newOrder = sortConfig.field === field && sortConfig.order === 'asc' ? 'desc' : 'asc';
    onSortChange({ field, order: newOrder });
  };

  const ariaSort = (field: SortField): 'ascending' | 'descending' | 'none' => {
    if (!sortConfig || sortConfig.field !== field) return 'none';
    return sortConfig.order === 'asc' ? 'ascending' : 'descending';
  };

  /** A header that sorts, or — when the page passes no sort handler — a plain label. */
  const sortableHeader = (field: SortField, label: string) => {
    if (!onSortChange) {
      return (
        <th scope="col" className={HEADER_CELL}>
          {label}
        </th>
      );
    }

    const active = sortConfig?.field === field;
    return (
      <th scope="col" className={HEADER_CELL} aria-sort={ariaSort(field)}>
        <button type="button" className={SORT_BUTTON} onClick={() => handleSort(field)}>
          {label}
          {active ? (
            sortConfig?.order === 'asc' ? (
              <CaretUp size={12} aria-hidden="true" />
            ) : (
              <CaretDown size={12} aria-hidden="true" />
            )
          ) : null}
        </button>
      </th>
    );
  };

  if (projects.length === 0) {
    return (
      <div className={TABLE_CARD}>
        <EmptyState
          icon={<FolderOpen size={28} aria-hidden="true" />}
          title={t('projects.noProjects')}
          message={t('projects.noProjectsMessage')}
        />
      </div>
    );
  }

  return (
    // `[contain:paint]` keeps the table's own horizontal scroll from leaking into the document's
    // scroll width, which is what made the whole page slide sideways on a phone.
    <div className={`${TABLE_CARD} [contain:paint]`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={TABLE_HEADER_ROW}>
              <th scope="col" className={`${HEADER_CELL} w-12`}>
                <SelectionCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onSelectAll}
                  aria-label={t('home.selectAllProjects')}
                />
              </th>
              <th scope="col" className={HEADER_CELL}>
                {t('projects.preview')}
              </th>
              {sortableHeader('title', t('home.sortTitle'))}
              {sortableHeader('part_type', t('home.columnPartType'))}
              {sortableHeader('created_at', t('projects.created'))}
              <th scope="col" className={HEADER_CELL}>
                {t('home.columnOwner')}
              </th>
              <th scope="col" className={HEADER_CELL}>
                {t('home.columnRuleSet')}
              </th>
              <th scope="col" className={HEADER_CELL}>
                {t('home.columnModels')}
              </th>
              <th scope="col" className={HEADER_CELL}>
                {t('home.columnCollision')}
              </th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              const previewUrl = project.model_png_url || project.stock_png_url;
              const modelId = extractModelIdFromUrl(previewUrl);
              const userName = formatUserName(project);
              const isSelected = selectedProjectIds.has(project.id);
              const ruleSet = projectRuleSetInfo(project, ruleSets);
              const hintClass = `project-apt-error-row-${project.id}`;

              return (
                <tr
                  key={project.id}
                  className={`${TABLE_ROW} cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`}
                  onClick={() => onProjectClick(project.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onProjectClick(project.id);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td className={TABLE_CELL}>
                    <SelectionCheckbox
                      checked={isSelected}
                      onChange={(selected) => onProjectSelect(project.id, selected)}
                      aria-label={t('home.selectProject')}
                    />
                  </td>
                  <td className={TABLE_CELL}>
                    {/* `ModelPreview` paints its image `object-cover`, which crops a wide part to
                        nothing at this size; the descendant selector wins over the utility on the
                        image itself, so the thumbnail fits the whole model instead. */}
                    <div className="h-10 w-14 overflow-hidden rounded bg-gray-50 [&_img]:object-contain">
                      {modelId ? (
                        <ModelPreview modelId={modelId} alt={`Preview of ${project.title}`} disableGif={true} />
                      ) : null}
                    </div>
                  </td>
                  <td className={TABLE_CELL}>
                    <div className="flex items-center gap-2">
                      {project.apt_generation_error && (
                        <>
                          <Tooltip target={`.${hintClass}`} position="bottom" style={{ maxWidth: '24rem' }} />
                          <span
                            className={`${hintClass} flex shrink-0 cursor-help items-center justify-center text-red-600`}
                            data-pr-tooltip={project.apt_generation_error}
                            aria-label={project.apt_generation_error}
                          >
                            <WarningCircle size={16} aria-hidden="true" />
                          </span>
                        </>
                      )}
                      <span className="truncate font-medium text-gray-900">{project.title}</span>
                    </div>
                  </td>
                  <td className={TABLE_CELL}>
                    {project.part_type ? (
                      <span className={CHIP}>{project.part_type}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className={TABLE_CELL}>
                    <span className="flex items-center gap-2 text-gray-500">
                      <CalendarBlank size={16} className="shrink-0" aria-hidden="true" />
                      <span className="whitespace-nowrap">{formatDateTime(project.created_at)}</span>
                    </span>
                  </td>
                  <td className={TABLE_CELL}>
                    {onUserClick ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUserClick(project.owner_id);
                        }}
                        className={`flex max-w-[180px] items-center gap-2 rounded text-sm text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline active:scale-[0.98] ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`}
                        title={userName}
                      >
                        <User size={16} className="shrink-0" aria-hidden="true" />
                        <span className="truncate">{userName}</span>
                      </button>
                    ) : (
                      <span className="flex max-w-[180px] items-center gap-2 text-gray-500" title={userName}>
                        <User size={16} className="shrink-0" aria-hidden="true" />
                        <span className="truncate">{userName}</span>
                      </span>
                    )}
                  </td>
                  <td className={TABLE_CELL}>
                    {ruleSet ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <ListChecks size={16} className="shrink-0" aria-hidden="true" />
                        <span className="truncate">{ruleSet.label}</span>
                        {ruleSet.version !== null && (
                          <span className="shrink-0 text-xs text-gray-400">
                            {t('ruleSets.versionLabel', { version: ruleSet.version })}
                          </span>
                        )}
                        {ruleSet.changed && (
                          <span
                            className="shrink-0 text-amber-600"
                            title={t('ruleSets.modifiedSince', { version: ruleSet.currentVersion })}
                          >
                            <Warning size={16} aria-hidden="true" />
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className={TABLE_CELL}>
                    <div className="flex gap-2">
                      {project.model_png_url && <span className={CHIP}>{t('home.badgeModel')}</span>}
                      {project.stock_png_url && <span className={CHIP}>{t('home.badgeStock')}</span>}
                    </div>
                  </td>
                  <td className={TABLE_CELL}>
                    {project.collision_simulation_state ? (
                      <CollisionStatusTag state={project.collision_simulation_state} />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
